import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Navigate, useLocation, useParams } from "react-router-dom";
import { CookieConsent } from "./components/CookieConsent";
import AppLayout from "./components/layouts/AppLayout";
import PublicLayout from "./components/layouts/PublicLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ChatbotConfigProvider } from "./contexts/ChatbotConfigContext";
import { ChatbotProvider, useChatbot } from "./contexts/ChatbotContext";
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import create from 'zustand';
import About from "./pages/About";
import Careers from "./pages/Careers";
import Contact from "./pages/Contact";
import ForgotPassword from "./pages/ForgotPassword";
import HomePage from "./pages/Home";
import Integrations from "./pages/Integrations";
import LegalNotice from "./pages/legal/LegalNotice";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import MiniApps from "./pages/MiniApps";
import Partners from "./pages/Partners";
import Pricing from "./pages/Pricing";
import ResetPassword from "./pages/ResetPassword";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import Widget from "./pages/Widget";
import { initializeMonitoring } from "./utils/monitoring";
import { Toaster } from "react-hot-toast";
// Import de l'EventBus pour l'initialiser dès le démarrage
import './utils/eventBus';
import { ProductModalHandler } from "./components/products/ProductModalHandler";

// Define MiniAppModalState interface
interface MiniAppModalState {
  activeApp: string | null;
  filters: any | null;
  setActiveApp: (appId: string | null, filters?: any) => void;
}

// Create a simple version of the useMiniAppModal hook using zustand pattern
// This should be replaced with the actual implementation when available
const useMiniAppModal = create<MiniAppModalState>((set) => ({
  activeApp: null,
  filters: null,
  setActiveApp: (appId, filters = null) => set({ activeApp: appId, filters }),
}));

// Make it globally available for the widget
if (typeof window !== 'undefined') {
  window.useMiniAppModal = useMiniAppModal;
}

// Widget wrapper component to properly use useParams
const WidgetWrapper = () => {
  const { id: pathId } = useParams<{ id: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const queryId = params.get('id');
  
  // Use either the path ID or the query ID, ensuring it's string or undefined (not null)
  const finalId = pathId || queryId || undefined;
  return (
    <ChatbotProvider isWidget={true} widgetPublicId={finalId}>
      <ChatbotConfigProvider>
        <Widget />
      </ChatbotConfigProvider>
    </ChatbotProvider>
  );
};

// Protected routes wrapper
const ProtectedRoutes = () => {
  const { user } = useAuth();

  useEffect(() => {
    // Only initialize monitoring when we have a valid user
    if (user) {
      initializeMonitoring().catch((err) =>
        console.warn("Failed to initialize monitoring:", err)
      );
    }
  }, [user]);

  return (
    <ProtectedRoute>
      <ChatbotConfigProvider>
        <AppLayout/>
      </ChatbotConfigProvider>
    </ProtectedRoute>
  );
};

// Widget loader component
const FloatingWidget = () => {
  const location = useLocation();
  const publicPaths = ['/', '/about', '/mini-apps', '/integrations', '/careers', '/partners'];
  
  useEffect(() => {
    // Only load widget on public paths
    if (publicPaths.includes(location.pathname)) {
      // Ajouter un style CSS pour gérer la transparence
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        [id^="botmaker-chat-"], [id^="botmaker-container-"], [id^="botmaker-iframe-"] {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
      `;
      document.head.appendChild(styleElement);
      
      // Injecter le script du widget
      const script = document.createElement('script');
      script.src = "https://localhost:5174/widget.js";
      script.async = true;
      script.onload = () => {
        // Corriger la déclaration pour éviter les erreurs TypeScript
        if (window.initBotmaker) {
          window.initBotmaker('cd2d2829-7526-482a-a97a-3550d87c8d5f', {
            position: "bottom-right",
            offset: { x: 20, y: 20 },
            lang: "fr",
            theme: "dark" // Utiliser le thème sombre pour éviter le fond blanc
          });
        }
      };
      document.body.appendChild(script);

      return () => {
        // Cleanup widget when route changes
        const iframe = document.getElementById('botmaker-widget-iframe-9326787e-36b8-4344-a876-ed1fae1886a9');
        const container = document.getElementById('botmaker-container-9326787e-36b8-4344-a876-ed1fae1886a9');
        const overlay = document.getElementById('botmaker-overlay-9326787e-36b8-4344-a876-ed1fae1886a9');
        
        if (iframe) iframe.remove();
        if (container) container.remove();
        if (overlay) overlay.remove();
        
        // Remove the script tag and style tag
        const scriptTag = document.querySelector('script[src="https://botmaker-agency.ai/widget.js"]');
        if (scriptTag) scriptTag.remove();
        if (styleElement) styleElement.remove();
      };
    }
  }, [location.pathname]);

  return null;
};

// Nous ajoutons le ProductModalHandler au ContextWrapper pour accéder au contexte du chatbot
const ProductModalWrapper = () => {
  const { chatbotPublicId } = useChatbot();
  // Ne rendre le ProductModalHandler que si un chatbotPublicId est disponible
  return chatbotPublicId ? <ProductModalHandler chatbotPublicId={chatbotPublicId} /> : null;
};

// Extend Window interface to include useMiniAppModal
declare global {
  interface Window {
    useMiniAppModal?: typeof useMiniAppModal;
    initBotmaker?: (
      id: string, 
      config?: { 
        position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
        offset?: { x: number; y: number };
        theme?: "light" | "dark";
        lang?: string;
        [key: string]: any;
      }
    ) => void;
    botmakerConfig?: {
      id?: string;
      showNotificationBubble?: boolean;
      showWelcomeMessage?: boolean;
      [key: string]: any;
    };
  }
}

export default function App() {
  useEffect(() => {
    initializeMonitoring();
  }, []);
  
  // Use setActiveApp from useMiniAppModal, but in a way that's consistent with React hooks rules
  const setMiniAppActive = useMiniAppModal(state => state.setActiveApp);
  
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <ChatbotProvider>
          <ChatbotConfigProvider>
            <Routes>
              {/* Public routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/legal/notice" element={<LegalNotice />} />
                <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                <Route path="/legal/terms" element={<TermsOfService />} />
                <Route path="/mini-apps" element={<MiniApps />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/widget/:id" element={<WidgetWrapper />} />
                <Route path="/widget" element={<WidgetWrapper />} />
              </Route>

              {/* Protected routes */}
              <Route path="/app/*" element={<ProtectedRoutes />} />

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Floating widget for public pages */}
            <FloatingWidget />
            
            {/* Gestionnaire global de modales de produits dans le contexte du chatbot */}
            <ProductModalWrapper />
          </ChatbotConfigProvider>
        </ChatbotProvider>
      </SubscriptionProvider>
      <CookieConsent />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#2D3748',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </AuthProvider>
  );
}